import * as React from "react";

import "./share-targets.scss";

import {cs}               from "@Common/react/chain-services";
import {
    consumeContext,
    provideContext1
}                         from "@Common/react/context";
import {csArr}            from "@Common/react/cs-arr";
import {Invoke}           from "@Common/react/invoke";
import {keyed}            from "@Common/react/keyed";
import {Load}             from "@Common/react/load";
import {ConditionalValue} from "@Common/react/shadow/conditional-value";
import {cascadeFind}      from "@Common/utils/cascade";
import {flatten1}         from "@Common/utils/collections";
import {chain}            from "@Common/utils/fs";
import {upperCase1}       from "@Common/utils/strings";

import {BasicTarget} from "./share-targets/basic/basic-target";
import {RelinkModal} from "./share-targets/common/relink-modal";
import {MetaTarget}  from "./share-targets/meta/meta-target";

export const ShareTargets = ({isPlatformSelectedInitially=undefined, next}) => cs(
    ["targets", ({}, next) => cs(
        consumeContext("apis"),
        consumeContext("event"),
        ["idUnique", ({event}, next) => next(event ? event.value.idUnique : (sessionStorage.getItem("listingUniqueId") ? sessionStorage.getItem("listingUniqueId") : ""))],
        ["sourceType", ({idUnique}, next) => next(idUnique ? 'property' : '')],
        ["socialMediaStatus", ({apis}, next) => Load({
            fetch: () => apis.next.social.getSocialMediaStatus(),
            next,
        })],
        ["shadow", ({socialMediaStatus}, next1) => ConditionalValue({
            disabled: !socialMediaStatus,
            renderShadow: next1,
            next,
        })],
        ({socialMediaStatus}, next) => provideContext1("socialMediaStatus", socialMediaStatus, next),
        ({apis, shadow, idUnique, sourceType}) => csArr([
            (next) => MetaTarget({
                isPlatformSelectedInitially,
                shareFb: ({url, comment, ccImage, account, page, date_scheduled, id_group_post}) => {
                    if (sessionStorage.DEBUG_FAIL_FB_SHARE === "1") {
                        return {
                            action: false,
                            error: "Share Failed. This post has already been shared to this account and cannot be reshared. Please change messaging for post and try again.",
                        };
                    }

                    const pageIndex = account.data.fb_pages.indexOf(page);

                    return apis.next.social.socialPostV2({
                        url, comment, date_scheduled,
                        image: ccImage,
                        socialSite: "Facebook",
                        pageIndex,
                        id_group_post,
                        source_id: idUnique,
                        source_type: sourceType,
                    });
                },
                shareIg: ({url, comment, ccImage, date_scheduled, id_group_post}) => {

                    if (sessionStorage.DEBUG_FAIL_IG_SHARE === "1") {
                        return {
                            action: false,
                            error: "Share Failed. This post has already been shared to this account and cannot be reshared. Please change messaging for post and try again.",
                        };
                    }

                    return apis.next.social.socialPostV2({
                        url, comment, date_scheduled,
                        image: ccImage,
                        socialSite: "Instagram",
                        id_group_post,
                        source_id: idUnique,
                        source_type: sourceType,
                    });
                },
                next,
            }),
            (next) => BasicTarget({
                type: "twitter",
                typeName: "X",
                initSelected: isPlatformSelectedInitially?.("twitter"),
                getAccountValue: (account) => account.tw_account,
                getAccount   : () => apis.next.social.getTwSocial(),
                removeAccount: () => apis.next.social.removeTwSocial(),
                invokeShare: ({url, ccImage, comment, date_scheduled, id_group_post}) => {

                    if (sessionStorage.DEBUG_FAIL_TW_SHARE === "1") {
                        return {
                            action: false,
                            error: "Share Failed. This post has already been shared to this account and cannot be reshared. Please change messaging for post and try again.",
                        };
                    }

                    return apis.next.social.socialPostV2({
                        url, comment, date_scheduled,
                        image: ccImage,
                        socialSite: "Twitter",
                        id_group_post,
                        source_id: idUnique,
                        source_type: sourceType,
                    });
                },
                next: (target) => next(target && {
                    ...target,
                    ...target.sharable && {
                        getRestrictCommentLength: ({url}) => {
                            const maxLength = 280 - (url?.length ? url.length + 1 : 0);
                            return {
                                maxLength,
                                message: `X comment will be truncated to ${maxLength} characters to accommodate the URL`,
                            };
                        },
                    },
                }),
            }),
            (next) => cs(

                ["relinkModal", (_, next) => RelinkModal({next})],
                ["doRelink", (_, next) => next(async (account) => {
                    await apis.next.social.removeLiSocial();

                    window.location = account.login_url;
                })],
                ({relinkModal, doRelink}) => BasicTarget({
                    type: "linkedin",
                    initSelected: isPlatformSelectedInitially?.("linkedin"),
                    getAccountValue: (account) => account.li_account,
                    getAccount   : () => apis.next.social.getLiSocial(),
                    removeAccount: () => apis.next.social.removeLiSocial(),
                    invokeShare: ({url, ccImage, comment, date_scheduled, id_group_post}) => {

                        if (sessionStorage.DEBUG_FAIL_LI_SHARE === "1") {
                            return {
                                action: false,
                                error: "Share Failed. This post has already been shared to this account and cannot be reshared. Please change messaging for post and try again.",
                            };
                        }

                        return apis.next.social.socialPostV2({
                            url, comment, date_scheduled,
                            image: ccImage,
                            socialSite: "LinkedIn",
                            id_group_post,
                            source_id: idUnique,
                            source_type: sourceType,
                        });
                    },
                    getMessage: (account) => account?.shouldRelink && "Please remove and relink your LinkedIn account to continue.",
                    extraRender: ({account, unselected, loggedIn, onRemoveAccount}) => (
                        loggedIn && !unselected.value && account?.shouldRelink && Invoke({
                            action: async () => {
                                const wantRelink = await relinkModal.show();
                                if (!wantRelink) {
                                    unselected.onChange(true);
                                } else if (wantRelink.removeLinkedIn) {
                                    await onRemoveAccount();
                                } else { // relink
                                    doRelink(account);
                                }
                            },
                        })
                    ),

                    next,
                }),
            ),
            (next) => BasicTarget({
                type: "pinterest",
                // color: "#c8232c",
                initSelected: isPlatformSelectedInitially?.("pinterest"),
                getAccountValue: (account) => account.pi_account,
                getAccount   : () => apis.next.social.getPiSocial(),
                removeAccount: () => apis.next.social.removePiSocial(),
                invokeShare: ({url, ccImage, comment, date_scheduled, id_group_post}) => {

                    if (sessionStorage.DEBUG_FAIL_PI_SHARE === "1") {
                        return {
                            action: false,
                            error: "Share Failed. This post has already been shared to this account and cannot be reshared. Please change messaging for post and try again.",
                        };
                    }

                    return apis.next.social.socialPostV2({
                        url, comment, date_scheduled,
                        image: ccImage,
                        socialSite: "Pinterest",
                        id_group_post,
                        source_id: idUnique,
                        source_type: sourceType,
                    });
                },
                next,
            }),
        ], shadow),
    )],
    ({targets}) => {
        const targets1 = targets?.filter(v=>v) ?? [];
        return next({
            getRestrictCommentLength: ({url}) => cascadeFind(targets1, "[*].getRestrictCommentLength")?.({url}),
            available: !!targets1.length,
            hasSelectedTargets: () => targets1.some((t) => t.sharable),
            getSelectedPlatformNames: () => targets1.filter((t) => t.sharable).map((t) => upperCase1(t.displayName)),
            getSharePieces: () => chain(
                targets1.filter((t) => t.sharable),
                (_) => _.map((t) => t.getSharePieces()),
                flatten1,
            ),
            getUnselectedPlatforms: () => targets1.map((t) => t.getUnselectedPlatforms()).flat(),
            render: () => (
                <div className="share-targets-45r">
                    {targets1.map((t) => cs(
                        keyed(t.key),
                        ({}) => (
                            <div className="target">
                                {t.render({})}
                            </div>
                        ),
                    ))}
                </div>
            ),
        });
    },
);
